import { PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { SKIP_DEV_JWT_CLAIMS_HEADER } from '@eureka/startup/src/enable-token-base';
import type { ResearchMcpApi } from '../research-mcp-api';
import type { McpToolResponse } from '../../types';

interface JsonRpcToolResult<T> {
    content?: Array<{ type: string; text?: string }>;
    structuredContent?: T;
    isError?: boolean;
}

interface JsonRpcResponse<T> {
    jsonrpc: '2.0';
    id: number;
    result?: JsonRpcToolResult<T>;
    error?: {
        code: number;
        message: string;
        data?: unknown;
    };
}

let requestId = 0;

function extractSummary<T>(result: JsonRpcToolResult<T>): string {
    return result.content
        ?.filter(item => item.type === 'text' && item.text)
        .map(item => item.text)
        .join('\n') || '';
}

export function parseToolResponse<T>(response: JsonRpcResponse<T>): McpToolResponse<T> {
    if (response.error) {
        throw new Error(`MCP JSON-RPC ${response.error.code}: ${response.error.message}`);
    }
    if (!response.result) {
        throw new Error('MCP response does not contain a result.');
    }

    const summary = extractSummary(response.result);
    if (response.result.isError) {
        throw new Error(summary || 'MCP tool execution failed.');
    }
    if (!response.result.structuredContent) {
        throw new Error('MCP response does not contain structuredContent.');
    }

    return {
        data: response.result.structuredContent,
        raw: response,
        summary,
    };
}

@injectable()
export class ResearchMcpApiImpl extends PsHttpService implements ResearchMcpApi {
    get options() {
        return {
            baseURL: import.meta.env.DEV
                ? '/research-mcp-api'
                : `${import.meta.env.VITE_EUREKA_SERVICE_URI}/research-mcp-api/mcp`,
            headers: {
                Accept: 'application/json, text/event-stream',
                'Content-Type': 'application/json',
                ...(import.meta.env.DEV
                    ? { [SKIP_DEV_JWT_CLAIMS_HEADER]: '1' }
                    : {}),
            },
        };
    }

    async callTool<T>(
        name: string,
        args: Record<string, unknown>,
        options: { signal?: AbortSignal; timeoutMs: number }
    ): Promise<McpToolResponse<T>> {
        const response = await this.post<any>({
            url: '',
            data: {
                jsonrpc: '2.0',
                id: ++requestId,
                method: 'tools/call',
                params: {
                    name,
                    arguments: args,
                },
            },
            signal: options.signal,
            timeout: options.timeoutMs,
        }) as JsonRpcResponse<T>;

        return parseToolResponse(response);
    }
}
